import React from "react";
import { createPortal } from "react-dom";

// An option may carry an `icon` (any node, e.g. a person's <Avatar />). It is
// drawn before the label in the list and in the trigger; search still matches
// the label only. Options without one render exactly as before.
const ICON_ROW = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  maxWidth: "100%",
};

// Where a panel drawn in a portal goes: under its trigger, or over it when it
// won't fit there and there's more room above. Null while it's closed.
export function usePanelPosition(open, enabled, wrapRef, minPanelHeight = 180) {
  const [position, setPosition] = React.useState(null);

  React.useEffect(() => {
    if (!open || !enabled) {
      setPosition(null);
      return undefined;
    }
    const viewportPad = 8;
    const gap = 8;

    const updatePos = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;

      const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
      const spaceAbove = rect.top - viewportPad;
      const openUp = spaceBelow < minPanelHeight && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        minPanelHeight,
        Math.floor((openUp ? spaceAbove : spaceBelow) - gap),
      );

      setPosition({
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        top: openUp ? null : Math.round(rect.bottom + gap),
        bottom: openUp ? Math.round(window.innerHeight - rect.top + gap) : null,
        maxHeight,
      });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open, enabled, wrapRef, minPanelHeight]);

  return position;
}

export default function FancySelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled = false,
  emptyText = "No options",
  ariaLabel,
  usePortal = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const wrapRef = React.useRef(null);
  const portalPos = usePanelPosition(open, usePortal, wrapRef);
  const inputRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const panelId = React.useMemo(
    () => `fancy-panel-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const data = React.useMemo(
    () =>
      (options || []).map((o) => ({
        value: o.value,
        label: o.label ?? String(o.value ?? ""),
        icon: o.icon || null,
        search: `${(o.label ?? "").toLowerCase()} ${(
          o.value ?? ""
        ).toLowerCase()}`,
      })),
    [options],
  );

  const selected = React.useMemo(
    () => data.find((d) => d.value === value) || null,
    [data, value],
  );

  const filtered = React.useMemo(() => {
    if (!q.trim()) return data;
    const k = q.trim().toLowerCase();
    return data.filter((d) => d.search.includes(k));
  }, [data, q]);

  React.useEffect(() => {
    function onDoc(e) {
      const inWrap = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inWrap && !inPanel) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else {
      setQ("");
      setActive(0);
    }
  }, [open]);

  function commit(val) {
    onChange?.(val);
    setOpen(false);
    setQ("");
  }

  function onTriggerKey(e) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onListKey(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[active];
      if (pick) commit(pick.value);
    }
  }

  const renderPanel = (style) => (
    <div
      ref={panelRef}
      id={panelId}
      className="selectPanel"
      role="listbox"
      style={style}
      onKeyDown={onListKey}
    >
      <div style={{ padding: 6 }}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          placeholder="Search..."
          aria-label="Search options"
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid var(--border, rgba(255,255,255,.12))",
            background:
              "linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03))",
            color: "var(--text, #EEEAF8)",
            outline: "none",
            fontSize: 16,
            minHeight: 44,
          }}
        />
      </div>

      <div className="scrollable" style={{ padding: 6, paddingTop: 0 }}>
        {filtered.length === 0 ? (
          <div className="muted" style={{ padding: 10 }}>
            {emptyText}
          </div>
        ) : (
          filtered.map((opt, i) => {
            const isSel = opt.value === value;
            const isActive = i === active;
            return (
              <div
                key={`${opt.value}`}
                role="option"
                aria-selected={isSel}
                className={`selectOption ${isSel ? "selected" : ""} ${
                  isActive ? "active" : ""
                }`}
                onMouseEnter={() => setActive(i)}
                onClick={() => commit(opt.value)}
              >
                {opt.icon ? (
                  <span style={ICON_ROW}>
                    {opt.icon}
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {opt.label}
                    </span>
                  </span>
                ) : (
                  opt.label
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`select ${open ? "open" : ""}`}
      ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
    >
      <button
        type="button"
        className="selectTrigger"
        data-placeholder={selected ? "false" : "true"}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        disabled={disabled}
        title={selected ? selected.label : placeholder}
        style={{
          width: "100%",
          opacity: disabled ? 0.6 : 1,
          fontSize: 16,
          minHeight: 44,
        }}
      >
        {selected?.icon ? (
          <span style={{ ...ICON_ROW, flex: "1 1 auto" }}>
            {selected.icon}
            <span className="selectValue">{selected.label}</span>
          </span>
        ) : (
          <span className="selectValue">
            {selected ? selected.label : placeholder}
          </span>
        )}
        <span className="selectCaret" aria-hidden />
      </button>

      {usePortal
        ? open &&
          portalPos &&
          createPortal(
            renderPanel({
              position: "fixed",
              left: portalPos.left,
              width: portalPos.width,
              // "auto", not undefined: .selectPanel sets a top of its own.
              top: portalPos.top ?? "auto",
              bottom: portalPos.bottom ?? "auto",
              maxHeight: portalPos.maxHeight,
              overflow: "auto",
              zIndex: 9000,
              opacity: 1,
              visibility: "visible",
              transform: "translateY(0) scale(1)",
            }),
            document.body,
          )
        : renderPanel({ maxHeight: 320, overflow: "auto", left: 0, right: 0 })}
    </div>
  );
}
